import { PlaidEntryPointInput, PlaidProduct } from '@varo/graphql-schema-types';
import { Redirect, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import handleEligibilityNavigation from 'features/advance/helpers/lightning/handleEligibilityNavigation';
import { getPlaidLoadingMessage } from 'features/advance/helpers/lightning/Lightning.helpers';
import useGetConsumerReportsAccount from 'features/advance/hooks/lightning/craUpgrade/useGetConsumerReportsAccount';
import useGetLightningEligibility from 'features/advance/hooks/lightning/useGetLightningEligibility';
import {
  AmountSelectionAlertState,
  AmountSelectionInlineAlertAtom,
} from 'features/advance/state/AmountSelectionInlineAlertAtom';
import { useSetAtom } from 'jotai';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useDefaultError from 'shared/hooks/errorFormatters/useDefaultError/useDefaultError';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import {
  PlaidCallbackError,
  PlaidIntent,
  PlaidIntentTypes,
} from 'shared/hooks/usePlaidV2/types';
import usePlaidV2 from 'shared/hooks/usePlaidV2/usePlaidV2';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

enum PlaidFlowType {
  UPGRADE = 'upgrade',
  LINKING = 'linking',
}

const getPlaidIntentInput = (
  accountId: string | undefined,
  plaidFlow: PlaidFlowType | undefined,
): PlaidIntentTypes => {
  switch (plaidFlow) {
    case PlaidFlowType.UPGRADE:
      return accountId
        ? { intent: PlaidIntent.CraUpgrade, accountId }
        : { intent: PlaidIntent.Wait };
    case PlaidFlowType.LINKING:
      return {
        intent: PlaidIntent.Link,
        entryPoint: PlaidEntryPointInput.Advance,
        product: PlaidProduct.Auth,
      };
    default:
      return { intent: PlaidIntent.Wait };
  }
};

const AdvanceConsumerReportPermissionScreen = () => {
  const router = useRouter();
  const { skipDdPrompt } = useLocalSearchParams<{ skipDdPrompt?: string }>();
  const shouldSkipDdPrompt = skipDdPrompt === 'true';
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [plaidFlow, setPlaidFlow] = useState<PlaidFlowType | undefined>(
    undefined,
  );
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [plaidSuccess, setPlaidSuccess] = useState(false);
  const [plaidCallbackError, setPlaidCallbackError] = useState<
    PlaidCallbackError | undefined
  >(undefined);
  const openPlaidInFlightRef = useRef(false);
  const setInlineAlertStatus = useSetAtom(AmountSelectionInlineAlertAtom);

  const { t } = useTranslation('advance', {
    keyPrefix: 'advanceConsumerReportPermissionScreenStrings',
  });
  const defaultError = useDefaultError();
  const dismissToTab = useDismissToRootTab();
  const { bottom } = useSafeAreaInsets();

  const {
    data: advanceConsumerReportPermissionScreen,
    fetching: initialLoadingScreen,
    error: getConsumerReportsAccountError,
  } = useGetConsumerReportsAccount({
    pause: false,
    requestPolicy: 'cache-and-network',
  });

  const craAccounts = advanceConsumerReportPermissionScreen?.permissionRequired
    ?.length
    ? advanceConsumerReportPermissionScreen.permissionRequired
    : null;

  const {
    openPlaid,
    ready,
    fetching,
    error: plaidError,
  } = usePlaidV2(
    // Stable intent identity keeps openPlaid stablec so the auto-open effect does not refire.
    // eslint-disable-next-line custom/no-manual-react-memoization
    useMemo(
      () => getPlaidIntentInput(accountId, plaidFlow),
      [accountId, plaidFlow],
    ),
    { sourceName: 'varo-advance/link-account/cra-upgrade' },
  );

  useEffect(() => {
    let isStaleRequest = false;

    const resetPlaidFlowState = () => {
      setAccountId(undefined);
      setPlaidFlow(undefined);
      setShowLoadingScreen(false);
    };

    const executeOpenPlaid = async () => {
      if (!ready || !plaidFlow || openPlaidInFlightRef.current) {
        return;
      }

      openPlaidInFlightRef.current = true;
      const result = await openPlaid();
      openPlaidInFlightRef.current = false;

      if (isStaleRequest) {
        return;
      }

      switch (result.status) {
        case 'success':
          setPlaidSuccess(true);
          setInlineAlertStatus((previous) =>
            previous.inlineAlertStatus ===
            AmountSelectionAlertState.AWAITING_LINK
              ? { inlineAlertStatus: AmountSelectionAlertState.SUCCESSFUL_LINK }
              : previous,
          );
          resetPlaidFlowState();
          return;
        case 'error':
          setPlaidCallbackError(result.error);
          resetPlaidFlowState();
          return;
        case 'exit':
          resetPlaidFlowState();
          return;
        default: {
          resetPlaidFlowState();
        }
      }
    };

    void executeOpenPlaid();

    return () => {
      isStaleRequest = true;
    };
  }, [ready, openPlaid, plaidFlow, setInlineAlertStatus]);

  const {
    fetching: fetchingEligibilityStatus,
    hasError: existingFlowHasError,
    counter,
    eligibilityStatus: status,
    data: { advanceProductsQualified },
    isFinalEligibilityStatus,
  } = useGetLightningEligibility({
    shouldPoll: plaidSuccess,
  });

  const loadingMessage = getPlaidLoadingMessage({ counter, translatedText: t });

  const hasError =
    getConsumerReportsAccountError ||
    existingFlowHasError ||
    plaidError ||
    plaidCallbackError;

  useEffect(() => {
    const shouldNavigate =
      isFinalEligibilityStatus &&
      status &&
      !fetchingEligibilityStatus &&
      !hasError;

    if (shouldNavigate) {
      handleEligibilityNavigation({
        status,
        advanceProductsQualified,
        skipDdPrompt: shouldSkipDdPrompt,
      });
    }
  }, [
    isFinalEligibilityStatus,
    fetchingEligibilityStatus,
    hasError,
    status,
    advanceProductsQualified,
    shouldSkipDdPrompt,
  ]);

  const handleBack = () => {
    if (router.canGoBack()) {
      return router.back();
    }

    router.replace('/');
  };

  const accountPressedAnalytics = usePressableAnalytics({
    analyticsId: 'cra-upgrade-account-pressed',
  });

  const linkAccountPressedAnalytics = usePressableAnalytics({
    analyticsId: 'cra-upgrade-link-new-account',
    customAttributes: {
      cta: 'Link a different account',
      subcategory: 'va_day_0',
    },
  });

  if (!craAccounts?.length && advanceConsumerReportPermissionScreen) {
    return <Redirect href="/" />;
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: (props) => (
            <ExpoNavigationBar {...props} primaryOnPress={handleBack} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={
          initialLoadingScreen ||
          (showLoadingScreen && !hasError) ||
          fetchingEligibilityStatus ||
          fetching
        }
        LoadingComponent={
          initialLoadingScreen ? (
            <LoadingSpinner />
          ) : (
            <FullScreenLoading
              isCoinLoader
              titleText={loadingMessage}
              bodyText={t('loadingScreen.body')}
            />
          )
        }
        ErrorComponent={
          hasError ? (
            <ErrorScreen
              screenAnalyticsEventKey="/varo-advance/link-account/error"
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultError.title}
              messageBody={defaultError.message}
              primaryButton={{
                title: t('error.button'),
                onPress: () => dismissToTab(Tabs.HOME),
              }}
            />
          ) : null
        }
      />
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <TitleLgText numberOfLines={0}>
              {advanceConsumerReportPermissionScreen?.title}
            </TitleLgText>
            <BodyBaseText numberOfLines={0} style={styles.subtitle}>
              {advanceConsumerReportPermissionScreen?.body}
            </BodyBaseText>
          </View>
          <View style={styles.accountList}>
            {craAccounts?.map((account, i) => (
              <Fragment key={account.accountId}>
                <ListItem
                  primaryCopy={account.primaryCopy}
                  showsChevron
                  leadingAccessory={{
                    id: VXLComponentId.ICON_AVATAR,
                    name: VXLComponentId.ICON_AVATAR,
                    props: {
                      variant: 'MD',
                      imageSource: { uri: account.iconUrl },
                    },
                  }}
                  onPress={() => {
                    accountPressedAnalytics();
                    setAccountId(account.accountId);
                    setPlaidFlow(PlaidFlowType.UPGRADE);
                    setShowLoadingScreen(true);
                  }}
                />
                {i < craAccounts.length - 1 ? <HairLineDivider /> : null}
              </Fragment>
            ))}
          </View>
        </ScrollView>
        <View
          style={[styles.footer, { paddingBottom: bottom + theme.spacing.m }]}
        >
          <Button
            label={t('buttons.primary')}
            type="SECONDARY"
            onPress={() => {
              linkAccountPressedAnalytics();
              setPlaidFlow(PlaidFlowType.LINKING);
              setShowLoadingScreen(true);
            }}
          />
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  accountList: {
    width: '100%',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  footer: {
    borderTopColor: theme.color.border.primary,
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  header: {
    gap: theme.spacing.s,
    paddingBottom: theme.spacing.l,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.l,
  },
  scrollContent: {
    flexGrow: 1,
  },
  subtitle: {
    color: theme.color.text.primary,
  },
});

export default AdvanceConsumerReportPermissionScreen;
